import httpService from "./httpService";

export interface AdvisorAccount {
  id: number;
  broker: string;
  accountId: string;
  serviceApiHost: string;
  userId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdvisorAccountDto {
  broker: string;
  accountId: string;
  serviceApiHost: string;
}

export interface UpdateAdvisorAccountDto {
  broker?: string;
  accountId?: string;
  serviceApiHost?: string;
  userId?: string | null;
}

export interface SubaccountsV2Metadata {
  total: number;
  pageSize: number;
  pageNum: number;
}

export interface IbkrSubaccountParent {
  mmc: string[];
  accountId: string;
  isMParent: boolean;
  isMChild: boolean;
  isMultiplex: boolean;
}

export interface IbkrSubaccount {
  id: string;
  accountId: string;
  accountVan: string;
  accountTitle: string;
  displayName: string;
  accountAlias: string | null;
  accountStatus: number;
  currency: string;
  type: string;
  tradingType: string;
  businessType: string;
  category: string;
  ibEntity: string;
  faclient: boolean;
  clearingStatus: string;
  covestor: boolean;
  noClientTrading: boolean;
  trackVirtualFXPortfolio: boolean;
  acctCustType: string;
  brokerageAccess: boolean;
  "PrepaidCrypto-Z": boolean;
  "PrepaidCrypto-P": boolean;
  desc: string;
  parent: IbkrSubaccountParent;
}

export interface SubaccountsV2Response {
  metadata: SubaccountsV2Metadata;
  subaccounts: IbkrSubaccount[];
}

export interface AdvisorClientAccount {
  id: string;
  userId: string;
  advisorAccountId: number;
  ibkrAccountId: string;
  accountTitle: string;
  accountType: string;
  currency: string;
  clearingStatus: string;
}

interface SubaccountsCache {
  subaccounts: IbkrSubaccount[];
  fetchedAt: number;
}

const SUBACCOUNTS_CACHE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours
const SUBACCOUNTS_PAGE_SIZE = 1000;

let subaccountsCache: SubaccountsCache | null = null;

const advisorAccountService = {
  getAdvisorAccounts: async (): Promise<AdvisorAccount[]> => {
    const response = await httpService.get("/v2/admin/advisor-accounts");
    return response.data;
  },

  getAdvisorAccountById: async (accountId: string): Promise<AdvisorAccount> => {
    const response = await httpService.get(`/v2/admin/advisor-accounts/${accountId}`);
    return response.data;
  },

  createAdvisorAccount: async (data: CreateAdvisorAccountDto): Promise<AdvisorAccount> => {
    const response = await httpService.post("/v2/admin/advisor-accounts", data);
    return response.data.account;
  },

  updateAdvisorAccount: async (id: number, data: UpdateAdvisorAccountDto): Promise<AdvisorAccount> => {
    const response = await httpService.patch(`/v2/admin/advisor-accounts/${id}`, data);
    return response.data.account;
  },

  deleteAdvisorAccount: async (id: number): Promise<void> => {
    await httpService.delete(`/v2/admin/advisor-accounts/${id}`);
  },

  getSubaccountsV2Page: async (pageNum: number): Promise<SubaccountsV2Response> => {
    const response = await httpService.get<SubaccountsV2Response>(
      `/v2/admin/advisor/subaccountsV2?pageNum=${pageNum}&pageSize=${SUBACCOUNTS_PAGE_SIZE}`
    );
    return response.data;
  },

  getCachedSubaccounts: (): IbkrSubaccount[] | null => {
    if (!subaccountsCache) return null;
    if (Date.now() - subaccountsCache.fetchedAt > SUBACCOUNTS_CACHE_TTL_MS) return null;
    return subaccountsCache.subaccounts;
  },

  setCachedSubaccounts: (subaccounts: IbkrSubaccount[]): void => {
    subaccountsCache = { subaccounts, fetchedAt: Date.now() };
  },

  clearSubaccountsCache: (): void => {
    subaccountsCache = null;
  },

  /**
   * Fetch all subaccountsV2 pages sequentially with 5s delay.
   * Writes to cache on completion. Reports progress via callback.
   * `onPage` receives each page's rows as it lands so callers can render
   * incrementally instead of waiting for the final page.
   * Returns null if cancelled (distinct from [] which means zero subaccounts).
   */
  fetchAllSubaccounts: async (
    onProgress?: (loaded: number, total: number) => void,
    isCancelled?: () => boolean,
    onPage?: (batch: IbkrSubaccount[], loaded: number, total: number) => void,
  ): Promise<IbkrSubaccount[] | null> => {
    const firstPage = await advisorAccountService.getSubaccountsV2Page(0);
    if (isCancelled?.()) return null;

    const { total, pageSize } = firstPage.metadata;
    const totalPages = Math.ceil(total / pageSize);
    let all = [...firstPage.subaccounts];
    onProgress?.(all.length, total);
    onPage?.(firstPage.subaccounts, all.length, total);

    for (let page = 1; page < totalPages; page++) {
      if (isCancelled?.()) return null;
      await new Promise(resolve => setTimeout(resolve, 5000));
      if (isCancelled?.()) return null;

      const pageData = await advisorAccountService.getSubaccountsV2Page(page);
      if (isCancelled?.()) return null;

      all = [...all, ...pageData.subaccounts];
      onProgress?.(all.length, total);
      onPage?.(pageData.subaccounts, all.length, total);
    }

    advisorAccountService.setCachedSubaccounts(all);
    return all;
  },

  getLinkedAccount: async (userId: string): Promise<AdvisorClientAccount> => {
    const response = await httpService.get<AdvisorClientAccount>(
      `/v2/admin/user-accounts/${userId}/linked-account`
    );
    return response.data;
  },

  syncUserAccount: async (userId: string): Promise<{ success: boolean; data: AdvisorClientAccount }> => {
    const response = await httpService.post(`/v2/admin/user-accounts/${userId}/sync-account`);
    return response.data;
  },
};

export default advisorAccountService;
